import {
  CONSOLE_PROFILE,
  assertNoVerdict,
} from "@norkron/web-privileges-admin";
import { privzRouteFor, type PrivzRoute } from "@norkron/web-privileges";
import { apiRequest } from "./realAdapter";

/**
 * The `BffClient` seam `@norkron/web-privileges-admin` reaches a server through.
 *
 * ## Why this is the ONLY new logic in the access-admin surface
 *
 * The admin console knows operation NAMES and nothing else — no URL, no verb, no
 * envelope. `postgresz-privileges` publishes the other half: a route manifest
 * generated from its comptime `routes()` array, carrying `{op, method, path}` for
 * every route the mount serves. So the mapping is a LOOKUP, not a table: this file
 * never spells an operation name or a path, and it cannot drift from the server,
 * because the manifest ships with the resolver the server compiles.
 *
 * ⛔ The profile is `CONSOLE_PROFILE`, fixed by the lib. "Served" is a property of
 * the MOUNT, not of the lib: `customer` (17 routes) compiles out the role matrix,
 * the elevation board and the review campaigns; `ops_only` (13) compiles out the
 * whole `/v1/privileges/*` tier. Flattening the profiles would make half the
 * console render as mysteriously unavailable instead of saying why.
 *
 * ## Authority
 *
 * ⛔ The browser makes ZERO authorization decisions. `assertNoVerdict` screens
 * every outbound payload for a verdict-shaped key and refuses the request before
 * it leaves the tab. The WASM resolver on the other side of this console exists
 * to EXPLAIN a provenance and PREVIEW a change; the server re-resolves and
 * enforces on every single request and is the only authority.
 *
 * ## Transport
 *
 * `apiRequest` — the app's one `/api` transport, so this surface inherits the
 * session cookie, the CSRF double-submit echo, the rotated-token retry and the
 * `ApiError` envelope rather than becoming a fifth bespoke `fetch` wrapper.
 */

/** Raised when an op is not on `CONSOLE_PROFILE`'s mount — a wiring bug, not a 404. */
export class UnmountedPrivzOpError extends Error {
  readonly op: string;

  constructor(op: string) {
    super(
      `privz operation "${op}" is not served by the ${CONSOLE_PROFILE} profile — ` +
        `the console asked for a route this mount does not carry.`,
    );
    this.name = "UnmountedPrivzOpError";
    this.op = op;
  }
}

/** `{id}` / `{cap}` / `{person}` — the manifest's path-parameter syntax. */
const PATH_PARAM = /\{([a-z_]+)\}/g;

/**
 * Split a flat param object into the path substitutions the route names and the
 * remainder. The manifest's placeholder names ARE the param keys the lib sends
 * (`{id}` ← `id`, `{cap}` ← `cap`, `{person}` ← `person`), so no per-op mapping
 * exists to go stale.
 */
function bindPath(
  route: PrivzRoute,
  params: Record<string, unknown>,
): { path: string; rest: Record<string, unknown> } {
  const rest = { ...params };
  const path = route.path.replace(PATH_PARAM, (_match, key: string) => {
    const value = rest[key];
    if (value === undefined || value === null || value === "") {
      throw new Error(
        `privz operation "${route.op}" needs a "${key}" path parameter for ${route.path}`,
      );
    }
    delete rest[key];
    return encodeURIComponent(String(value));
  });
  return { path, rest };
}

/** Everything left after path binding, as `?a=b` pairs. Absent values are dropped. */
function toQuery(rest: Record<string, unknown>): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (v === undefined || v === null) continue;
    out[k] = String(v);
  }
  return out;
}

function paramsOf(params: unknown): Record<string, unknown> {
  if (params === undefined || params === null) return {};
  if (typeof params !== "object" || Array.isArray(params)) {
    throw new TypeError("privz operation params must be a plain object");
  }
  return { ...(params as Record<string, unknown>) };
}

async function call<T>(op: string, params: unknown): Promise<T> {
  // ⛔ FIRST, before anything is serialised: refuse a payload carrying a
  // client-computed verdict. A console that can send one is a console that will.
  assertNoVerdict(op, params);

  const route = privzRouteFor(CONSOLE_PROFILE, op);
  if (route === undefined) throw new UnmountedPrivzOpError(op);

  const { path, rest } = bindPath(route, paramsOf(params));
  // A GET carries its remainder as a query string; every write carries it as the
  // JSON body. The METHOD comes from the manifest, never from this file — which
  // is what stops a read being sent as a write (and skipping CSRF) by mistake.
  const isRead = route.method === "GET";
  return apiRequest<T>(path, {
    method: route.method,
    query: isRead ? toQuery(rest) : undefined,
    body: isRead || Object.keys(rest).length === 0 ? undefined : rest,
  });
}

/**
 * The transport handed to `PrivilegesAdminProvider`.
 *
 * Structurally a `@norkron/web-console-shell` `BffClient` — `{query, mutate}` —
 * without importing that package. ⛔ elaborate does NOT adopt `web-console-shell`:
 * its `NavTree` renders a raw `href`, which is a full page reload that kills the
 * SPA, and it carries a SCAFFOLD-STAGE banner. `web-privileges-admin` references
 * it with `import type` only and its `dist` has no runtime import of it, so
 * composing the provider directly is the supported path, not a workaround.
 */
export const privzBff = {
  query: <T,>(op: string, params: unknown): Promise<T> => call<T>(op, params),
  mutate: <T,>(op: string, params: unknown): Promise<T> => call<T>(op, params),
};
